import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/ui/dataTable/DataTable";
import { Pagination } from "../../components/ui/pagination/Pagination";
import { StatusBadge } from "../../components/ui/statusBadge/StatusBadge";
import { EmptyState } from "../../components/ui/emptyState/EmptyState";
import { useModal } from "../../components/ui/modal/ModalProvider";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { MOCK_DATA, bumpMockData, useMockDataVersion } from "../../data/mockDataStore";
import { date, datetime, money } from "../../utils/format";

const STATUTS = ["Confirmée", "En attente", "Annulée", "Terminée", "Remboursement demandé", "Remboursée"];
const PAGE_SIZE = 8;

export function Reservations() {
  const { id } = useParams();
  if (id) return <ReservationFiche id={id} />;
  return <ReservationsList />;
}

function ReservationsList() {
  useMockDataVersion();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refundOnly = searchParams.get("f") === "refund";
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [statut, setStatut] = useState("");
  const [etab, setEtab] = useState("");
  const [page, setPage] = useState(1);

  const etabOptions = useMemo(() => {
    const ids = [...new Set(MOCK_DATA.reservations.map((r) => r.etabId))];
    return ids.map((id) => {
      const e = MOCK_DATA.etablissements.find((x) => x.id === id);
      return { id, label: e ? e.nom : id };
    });
  }, []);

  const filtered = useMemo(() => {
    const qq = q.toLowerCase();
    return MOCK_DATA.reservations.filter((r) => {
      if (refundOnly && r.statut !== "Remboursement demandé") return false;
      if (statut && r.statut !== statut) return false;
      if (etab && r.etabId !== etab) return false;
      if (qq && !(r.id.toLowerCase().includes(qq) || r.voyageRef.toLowerCase().includes(qq) || r.client.toLowerCase().includes(qq))) return false;
      return true;
    });
  }, [q, statut, etab, refundOnly]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <a onClick={() => navigate("/dashboard")}>Dashboard</a>
            <i className="fa-solid fa-chevron-right"></i>Réservations
          </div>
          <h1 className="page-title">Réservations {refundOnly ? "· Remboursements" : ""}</h1>
          <div className="page-sub">Gérez les réservations et les demandes de remboursement</div>
        </div>
        <div className="page-actions">
          <button
            className={`btn ${refundOnly ? "" : "btn-primary"}`}
            data-testid="btn-toggle-refund"
            onClick={() => navigate(refundOnly ? "/reservations" : "/reservations?f=refund")}
          >
            <i className="fa-solid fa-rotate-left"></i> {refundOnly ? "Voir toutes" : "Remboursements en attente"}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input data-testid="resa-search" placeholder="Rechercher par réf. réservation ou voyage (R-… ou V-…)" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          </div>
          <select className="select" data-testid="resa-statut" value={statut} onChange={(e) => { setStatut(e.target.value); setPage(1); }}>
            <option value="">Tous les statuts</option>
            {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="select" data-testid="resa-etab" value={etab} onChange={(e) => { setEtab(e.target.value); setPage(1); }}>
            <option value="">Tous les établissements</option>
            {etabOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <select className="select" data-testid="resa-period"><option>Toutes périodes</option><option>7 derniers jours</option><option>30 derniers jours</option><option>90 derniers jours</option></select>
          <div className="filter-chips"><span className="badge outline"><i className="fa-solid fa-filter"></i> {filtered.length} résultat{filtered.length > 1 ? "s" : ""}</span></div>
        </div>

        <DataTable
          columns={[
            { label: "Références", render: (r) => <div><div className="mono strong">{r.id}</div><div className="muted mono">{r.voyageRef}</div></div> },
            { label: "Client", render: (r) => <div><div className="strong">{r.client}</div><div className="muted">{r.voyageurs} voyageur{r.voyageurs > 1 ? "s" : ""}</div></div> },
            { label: "Circuit", render: (r) => <div>{r.circuit}<div className="muted">{r.etab}</div></div> },
            { label: "Réservation", render: (r) => date(r.dateResa) },
            { label: "Départ", render: (r) => date(r.depart) },
            { label: "Montant", render: (r) => <span className="mono strong">{money(r.montant)}</span> },
            { label: "Statut", render: (r) => <StatusBadge statut={r.statut} /> },
          ]}
          rows={paged}
          onRowClick={(r) => navigate("/reservations/" + r.id)}
          rowTestIdPrefix="resa-row"
        />
        <Pagination total={filtered.length} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} />
      </div>
    </>
  );
}

function ReservationFiche({ id }) {
  useMockDataVersion();
  const navigate = useNavigate();
  const { confirm } = useModal();
  const { toast } = useToast();

  const r = MOCK_DATA.reservations.find((x) => x.id === id);
  if (!r) return <EmptyState title="Réservation introuvable" sub={id} />;

  const isRefund = r.statut === "Remboursement demandé";

  const acceptRefund = () => {
    confirm({
      title: "Accepter le remboursement",
      message: <>Confirmez le remboursement de <strong>{money(r.refund.montant)}</strong> pour la réservation <strong>{r.id}</strong>.</>,
      confirmText: "Accepter",
      type: "success",
      onConfirm: () => { r.statut = "Remboursée"; bumpMockData(); toast("Remboursement accepté", "success"); },
    });
  };

  const rejectRefund = () => {
    confirm({
      title: "Refuser le remboursement",
      message: "Confirmez le refus. Un message explicatif sera envoyé au client.",
      confirmText: "Refuser",
      type: "danger",
      onConfirm: () => { r.statut = "Confirmée"; bumpMockData(); toast("Demande refusée", "warn"); },
    });
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <a onClick={() => navigate("/reservations")}>Réservations</a>
            <i className="fa-solid fa-chevron-right"></i>{r.id}
          </div>
          <h1 className="page-title">Réservation <span className="mono">{r.id}</span></h1>
          <div className="page-sub">Voyage <span className="mono">{r.voyageRef}</span> · {r.circuit}</div>
        </div>
        <div className="page-actions">
          <StatusBadge statut={r.statut} />
          <button className="btn" data-testid="btn-msg-client"><i className="fa-regular fa-envelope"></i> Contacter le client</button>
        </div>
      </div>

      {isRefund ? (
        <div className="card" style={{ borderColor: "#FDE68A", background: "var(--warning-50)", marginBottom: 16 }}>
          <div className="card-body" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--warning)", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
              <i className="fa-solid fa-rotate-left"></i>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Demande de remboursement en attente</h3>
              <div style={{ color: "var(--text-2)", fontSize: 13.5 }}>Motif : <strong>{r.refund?.motif || "—"}</strong></div>
              <div style={{ color: "var(--text-2)", fontSize: 13.5, marginTop: 4 }}>Montant : <strong className="mono">{money(r.refund?.montant || 0)}</strong> · Reçue le {date(r.refund?.date)}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-success" data-testid="btn-accept-refund" onClick={acceptRefund}><i className="fa-solid fa-check"></i> Accepter</button>
              <button className="btn btn-danger" data-testid="btn-reject-refund" onClick={rejectRefund}><i className="fa-solid fa-xmark"></i> Refuser</button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="detail-grid">
        <div>
          <div className="card">
            <div className="card-header"><h3 className="card-title">Informations voyageur</h3></div>
            <div className="card-body">
              <div className="info-list">
                <div><div className="k">Nom du client</div><div className="v">{r.client}</div></div>
                <div><div className="k">Email</div><div className="v">{r.email}</div></div>
                <div><div className="k">Téléphone</div><div className="v">{r.tel}</div></div>
                <div><div className="k">Nombre de voyageurs</div><div className="v">{r.voyageurs}</div></div>
              </div>
            </div>
          </div>

          <div style={{ height: 16 }}></div>

          <div className="card">
            <div className="card-header"><h3 className="card-title">Informations voyage</h3></div>
            <div className="card-body">
              <div className="info-list">
                <div><div className="k">Circuit</div><div className="v">{r.circuit}</div></div>
                <div><div className="k">Référence voyage</div><div className="v mono">{r.voyageRef}</div></div>
                <div><div className="k">Établissement</div><div className="v"><a onClick={() => navigate("/etablissements/" + r.etabId)} style={{ color: "var(--primary)", cursor: "pointer" }}>{r.etab}</a></div></div>
                <div><div className="k">Date de réservation</div><div className="v">{date(r.dateResa)}</div></div>
                <div><div className="k">Date de départ</div><div className="v">{date(r.depart)}</div></div>
                <div><div className="k">Montant total</div><div className="v mono strong">{money(r.montant)}</div></div>
              </div>
            </div>
          </div>
        </div>

        <aside>
          <div className="card">
            <div className="card-header"><h3 className="card-title">Historique</h3></div>
            <div className="card-body">
              <div className="timeline">
                <div className="tl-item done"><div className="tl-time">{datetime(r.dateResa)}</div><div className="tl-title">Réservation créée</div></div>
                <div className="tl-item done"><div className="tl-time">{datetime(r.dateResa)}</div><div className="tl-title">Paiement reçu</div><div className="tl-desc">{money(r.montant)} · CB</div></div>
                {r.statut !== "Annulée" ? <div className="tl-item done"><div className="tl-time">{datetime(r.dateResa)}</div><div className="tl-title">Réservation confirmée</div></div> : null}
                {isRefund ? <div className="tl-item danger"><div className="tl-time">{datetime(r.refund.date)}</div><div className="tl-title">Remboursement demandé</div><div className="tl-desc">{r.refund.motif}</div></div> : null}
                {r.statut === "Remboursée" ? <div className="tl-item done"><div className="tl-time">{datetime(r.refund?.date)}</div><div className="tl-title">Remboursement effectué</div></div> : null}
                {r.statut === "Annulée" ? <div className="tl-item danger"><div className="tl-title">Réservation annulée</div></div> : null}
                {r.statut === "Terminée" ? <div className="tl-item done"><div className="tl-title">Voyage terminé</div></div> : null}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}

export default Reservations;
